import { useState, type FormEvent } from "react";
import { useList, useInvalidate } from "@refinedev/core";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/forms/PageHeader";
import { formatRelativeTime } from "@/lib/formatters";
import { supabase } from "@/lib/supabase";

interface Perfil {
  id: string;
  nombre_completo: string;
  rol: "admin_central" | "operador_tienda" | "contador";
  ultima_actividad: string | null;
  activo: boolean;
  tienda_id: string | null;
}

interface Tienda {
  id: string;
  nombre: string;
}

const ROL_LABEL = {
  admin_central: "Admin central",
  operador_tienda: "Operador tienda",
  contador: "Contador",
};

export function UsuariosPage() {
  const [showForm, setShowForm] = useState(false);
  const invalidate = useInvalidate();

  const { data, isLoading } = useList<Perfil>({
    resource: "perfiles",
    meta: { select: "*, tiendas(nombre)" },
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <PageHeader
          title="Usuarios"
          subtitle="Operadores y admins del sistema"
        />
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2">
          <Plus className="size-4" /> Nuevo usuario
        </button>
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-fg-muted">Cargando...</div>
      ) : (
        <ul className="space-y-2">
          {data?.data.map((u) => (
            <li key={u.id} className="card p-4 flex items-center gap-3">
              <div className="flex-1">
                <p className="font-medium">{u.nombre_completo}</p>
                <p className="text-sm text-fg-muted">
                  {ROL_LABEL[u.rol]}
                  {u.ultima_actividad && ` · Activo ${formatRelativeTime(u.ultima_actividad)}`}
                </p>
              </div>
              {!u.activo && <span className="text-xs text-error">Inactivo</span>}
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <NuevoUsuarioModal
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            invalidate({ resource: "perfiles", invalidates: ["list"] });
          }}
        />
      )}
    </div>
  );
}

function NuevoUsuarioModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [nombre, setNombre] = useState("");
  const [rol, setRol] = useState<"admin_central" | "operador_tienda" | "contador">("operador_tienda");
  const [tiendaId, setTiendaId] = useState("");
  const [enviando, setEnviando] = useState(false);

  const { data: tiendasData } = useList<Tienda>({
    resource: "tiendas",
    filters: [{ field: "activo", operator: "eq", value: true }],
    pagination: { mode: "off" },
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email || !nombre) {
      toast.error("Faltan campos obligatorios");
      return;
    }
    if (rol === "operador_tienda" && !tiendaId) {
      toast.error("Operadores deben tener tienda asignada");
      return;
    }
    setEnviando(true);
    try {
      // 1. Crear/invitar al usuario por magic link (Supabase enviará un correo
      //    con un link para que ponga su contraseña la primera vez).
      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${window.location.origin}/auth/recovery`,
          data: { nombre_completo: nombre },
        },
      });
      if (otpErr) throw otpErr;

      // 2. Buscar el auth.user recién creado para enlazar el perfil.
      //    No tenemos service_role, pero el INSERT con el email funciona via
      //    la función pública `crear_perfil_pendiente` (que el admin tiene permiso
      //    de ejecutar). Si esa función no existe, fallback a guardar pendiente.
      const { error: rpcErr } = await supabase.rpc("crear_perfil_para_email", {
        p_email: email,
        p_nombre: nombre,
        p_rol: rol,
        p_tienda_id: tiendaId || null,
      });
      if (rpcErr) {
        // La función no existe todavía. Avisamos al admin del flujo manual.
        toast.warning("Invitación enviada", {
          description:
            "El usuario recibirá un correo. Después de que entre y vuelva, edita su perfil para asignar rol/tienda.",
        });
      } else {
        toast.success("Usuario invitado y perfil vinculado");
      }
      onCreated();
    } catch (err) {
      toast.error("Error", { description: (err as Error).message });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg">Nuevo usuario</h2>
          <button onClick={onClose} aria-label="Cerrar">
            <X className="size-5" />
          </button>
        </div>

        <p className="text-sm text-fg-muted">
          Se enviará un correo con un link mágico al usuario para que cree su contraseña. Después podrá
          entrar a la app con su correo.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">Correo *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              required
              autoFocus
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Nombre completo *</label>
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="input"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Rol *</label>
            <select
              value={rol}
              onChange={(e) =>
                setRol(e.target.value as "admin_central" | "operador_tienda" | "contador")
              }
              className="input"
            >
              <option value="operador_tienda">Operador tienda</option>
              <option value="admin_central">Admin central</option>
              <option value="contador">Contador</option>
            </select>
          </div>

          {rol === "operador_tienda" && (
            <div className="space-y-1">
              <label className="text-sm font-medium">Tienda *</label>
              <select
                value={tiendaId}
                onChange={(e) => setTiendaId(e.target.value)}
                className="input"
                required
              >
                <option value="">Selecciona...</option>
                {tiendasData?.data.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={enviando} className="btn-primary flex-1">
              {enviando ? "Enviando..." : "Enviar invitación"}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
